
import { useState, useEffect } from 'react';
import { Expense, Balance, Settlement, PaymentInfo } from '@/types/expense';

export const useSettlements = (expenses: Expense[], profiles: Record<string, any>) => {
  const [balances, setBalances] = useState<Balance[]>([]);
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [paymentInfos, setPaymentInfos] = useState<Record<string, PaymentInfo>>({});

  // Load payment information from localStorage
  useEffect(() => {
    if (Object.keys(profiles).length === 0) return;
    
    const infos: Record<string, PaymentInfo> = {};
    
    Object.keys(profiles).forEach(userId => {
      const storedInfo = localStorage.getItem(`payment_info_${userId}`);
      if (storedInfo) {
        try {
          infos[userId] = JSON.parse(storedInfo) as PaymentInfo;
        } catch (e) {
          console.error(`Failed to parse payment info for user ${userId}`, e);
        }
      }
    });
    
    setPaymentInfos(infos);
  }, [profiles]);

  // Calculate balances and settlements
  useEffect(() => {
    if (expenses.length === 0 || Object.keys(profiles).length === 0) {
      return;
    }
    
    try {
      // Calculate how much each person has paid and owes
      const balanceMap: Record<string, number> = {};
      
      // Initialize balances for all users to 0
      Object.keys(profiles).forEach(userId => {
        balanceMap[userId] = 0;
      });
      
      // Process each expense
      expenses.forEach(expense => {
        const paidBy = expense.paid_by;
        const amount = expense.amount;
        
        // For now, we'll assume even splits only
        // Add the full amount to the person who paid
        balanceMap[paidBy] += amount;
        
        // Calculate the share for each person
        const share = amount / Object.keys(profiles).length;
        
        // Subtract each person's share from their balance
        Object.keys(profiles).forEach(userId => {
          balanceMap[userId] -= share;
        });
      });
      
      // Convert the balance map to an array
      const balanceArray: Balance[] = Object.entries(balanceMap).map(([userId, amount]) => ({
        user_id: userId,
        name: profiles[userId]?.username || 'Unknown User',
        amount: parseFloat(amount.toFixed(2)),
      }));
      
      setBalances(balanceArray);
      
      // Calculate the settlements
      calculateSettlements(balanceArray);
    } catch (error) {
      console.error('Error calculating balances:', error);
    }
  }, [expenses, profiles]);

  const calculateSettlements = (balances: Balance[]) => {
    // Separate into debtors and creditors
    const debtors = balances.filter(b => b.amount < 0)
      .sort((a, b) => a.amount - b.amount);
    const creditors = balances.filter(b => b.amount > 0)
      .sort((a, b) => b.amount - a.amount);
    
    const result: Settlement[] = [];
    
    // For each debtor, find creditors to pay
    debtors.forEach(debtor => {
      let remaining = Math.abs(debtor.amount);
      
      while (remaining > 0 && creditors.length > 0) {
        const creditor = creditors[0];
        const amount = Math.min(remaining, creditor.amount);
        
        result.push({
          from: debtor.user_id,
          fromName: debtor.name,
          to: creditor.user_id,
          toName: creditor.name,
          amount: parseFloat(amount.toFixed(2)),
        });
        
        remaining -= amount;
        creditor.amount -= amount;
        
        // If creditor is fully paid, remove them
        if (creditor.amount <= 0.01) {
          creditors.shift();
        }
      }
    });
    
    setSettlements(result);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return {
    balances,
    settlements,
    paymentInfos,
    formatCurrency
  };
};
